import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

// Helpers
import { setObjectsList } from "@/Common/helpers/objects";

// Components
import SearchInput from "./SearchInput";

// Local functions helpers
import { isRowMatchSearch } from "./utils";

const isValidInternalSearch = ({ searchValue, disabled }) => !searchValue && !disabled;

const getUniqueIdentifier = (row) => (row?._id ? "_id" : "id");

const getUniqueRows = (data = []) => {
	if (!data?.length) {
		return [];
	}
	const uniqueKey = getUniqueIdentifier(data?.[0]);
	return setObjectsList(data, [uniqueKey]);
};

const hasChildren = (row, data = [], field = "hierarchy") => {
	if (!row || !data?.length) {
		return false;
	}

	const key = getUniqueIdentifier(row);
	return data.some((item) => {
		if (item?.[key] === row[key]) {
			return false;
		}
		return item?.[field].includes(row[key]);
	});
};

const isChildPresent = (row, keys = [], identifier = "id") => keys.some((key) => key === row[identifier]);

const getNestedFilteredData = (data = [], results = []) => {
	if (!data?.length || !results?.length) {
		return results;
	}

	const parents = results?.filter((row) => !row?.id?.startsWith("child_") || !row?._id?.startsWith("child_"));
	const keys = parents?.map((row) => row?.id || row?._id);

	const identifier = getUniqueIdentifier(data[0]);
	const child = data?.filter((row) => [...new Set(keys)].some((key) => key === row[identifier]));
	return getUniqueRows([...parents, ...child]);
};

const getNoEmptyParents = (parents, { identifier, data = [], keys = [] }) => {
	if (!parents?.length) {
		return [];
	}

	return parents.filter((row) => {
		const isPresent = isChildPresent(row, keys, identifier);
		if (isPresent) {
			return true;
		}
		return !hasChildren(row, data);
	});
};

const getParentsFilteredData = (data = [], results = [], noEmptyParents = false) => {
	if (!data?.length || !results?.length) {
		return results;
	}

	const isRoot = (row) => row?.["hierarchy"]?.length === 1;
	const child = results?.filter((row) => (row?.["hierarchy"]?.length || 0) > 1 || !isRoot(row));
	const keys = child?.map((row) => row?.["hierarchy"]?.[0]);

	const identifier = getUniqueIdentifier(data[0]);
	const resultParents = results?.filter((row) => isRoot(row));
	const parents = data?.filter((row) => [...new Set(keys)].some((key) => key === row[identifier]));

	let parentsResults = [...parents, ...resultParents];
	if (noEmptyParents) {
		parentsResults = getNoEmptyParents(parentsResults, { identifier, data, keys });
	}

	return getUniqueRows([...parentsResults, ...child]);
};

const getSearchResults = ({ nested, parents, noEmptyParents, data = [], columns = [], value = "", excludeKeys = [], nestedKeys = [] }) => {
	if (!data?.length || !value) {
		return data;
	}
	const dataFiltrable = columns.length > 0 ? dataSearchable(data, columns) : data;
	let results = dataFiltrable?.filter((row) => isRowMatchSearch({ row, value, columns, excludeKeys, nestedKeys }));

	// Locate nested (children) behavior
	if (nested) {
		results = getNestedFilteredData(data, results);
	}

	// Locate parents (root) behavior
	if (parents) {
		results = getParentsFilteredData(data, results, noEmptyParents);
	}

	return results;
};

function dataSearchable(data, columnDefinitions) {
	const noRequiredColumns = ["actions"];

	return data.map((row) => {
		const newRow = {};
		columnDefinitions.forEach((column) => {
			if (
				column.field === "_id" ||
				column.field === "id" ||
				(!noRequiredColumns.find((col) => column.field === col && column?.getActions) && !column?.hide)
			) {
				newRow[column.field] = column.valueFormatter ? column.valueFormatter(row[column.field]) : row[column.field];
			}
		});
		return newRow;
	});
}

export default function SearchToolbar(props) {
	const {
		data = [],
		excludeKeys = [],
		apiRef,
		columns,
		searchValue,
		disabled,
		parents,
		noEmptyParents,
		nested,
		nodeExpandedIds,
		searchInNestedArrays,
		setNodeExpandedIds,
		onChange,
	} = props;

	const mandatoryExcludeKeys = [...new Set([...(excludeKeys || []), "id", "_id", "hierarchy"])];
	const [value, setValue] = useState("");

	const resetRowsHeights = useCallback(
		(apiReference) => {
			if (!nodeExpandedIds?.length || !apiReference?.current) {
				return;
			}

			nodeExpandedIds?.forEach((nodeId) => {
				const rowNode = apiReference?.current?.getRowElement(nodeId);
				if (rowNode) {
					rowNode.classList.remove("expand-table-row");
				}
			});

			setNodeExpandedIds?.([]);
		},
		[nodeExpandedIds, setNodeExpandedIds]
	);

	const onClearEvent = () => {
		setValue("");
		apiRef?.current?.setRows(data);
		onChange?.("");
	};

	const onHandlerChange = (event) => {
		const updatedValue = event.target.value || "";
		setValue(updatedValue);
		onChange?.(updatedValue);
	};

	const isValidSearch = isValidInternalSearch({ searchValue, disabled });
	const results = getSearchResults({
		nested,
		parents,
		noEmptyParents,
		columns,
		data: [...data],
		excludeKeys: mandatoryExcludeKeys,
		value: value || searchValue,
		nestedKeys: searchInNestedArrays,
	});

	useEffect(() => {
		apiRef?.current?.setRows(results);
		resetRowsHeights(apiRef);
	}, [results, apiRef, resetRowsHeights]);

	useEffect(() => {
		if (!isValidSearch) {
			setValue("");
		}

		return () => {
			setValue("");
		};
	}, [data, searchValue, isValidSearch]);

	return <SearchInput value={value} disabled={!isValidSearch} onClear={onClearEvent} onChange={onHandlerChange} />;
}

SearchToolbar.propTypes = {
	apiRef: PropTypes.shape({
		current: PropTypes.object,
	}),
	data: PropTypes.array,
	columns: PropTypes.array,
	searchValue: PropTypes.string,
	disabled: PropTypes.bool,
	parents: PropTypes.bool,
	noEmptyParents: PropTypes.bool,
	nested: PropTypes.bool,
	nodeExpandedIds: PropTypes.array,
	excludeKeys: PropTypes.array,
	searchInNestedArrays: PropTypes.array,
	setNodeExpandedIds: PropTypes.func,
	onChange: PropTypes.func,
};
